// MapPicker.tsx — Clickable Leaflet map for picking project location
import React, { useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';

interface MapPickerProps {
    latitude: string;
    longitude: string;
    onLocationPick: (lat: number, lng: number) => void;
}

const MapPicker: React.FC<MapPickerProps> = ({ latitude, longitude, onLocationPick }) => {
    const mapRef = useRef<any>(null);
    const markerRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let L: any;
        let map: any;

        const init = async () => {
            L = await import('leaflet');

            // Fix default marker icons
            delete (L.Icon.Default.prototype as any)._getIconUrl;
            L.Icon.Default.mergeOptions({
                iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
                iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
                shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
            });

            if (!containerRef.current || mapRef.current) return;

            const defaultLat = parseFloat(latitude) || 23.0225;
            const defaultLng = parseFloat(longitude) || 72.5714;

            map = L.map(containerRef.current, { zoomControl: true }).setView([defaultLat, defaultLng], 13);
            mapRef.current = map;

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors'
            }).addTo(map);

            // Place initial marker if coords exist
            if (latitude && longitude) {
                markerRef.current = L.marker([defaultLat, defaultLng]).addTo(map)
                    .bindPopup('📍 Project Location').openPopup();
            }

            // On map click — place/move marker and call parent
            map.on('click', (e: any) => {
                const { lat, lng } = e.latlng;

                if (markerRef.current) {
                    markerRef.current.setLatLng([lat, lng])
                        .bindPopup(`📍 ${lat.toFixed(6)}, ${lng.toFixed(6)}`).openPopup();
                } else {
                    markerRef.current = L.marker([lat, lng]).addTo(map)
                        .bindPopup(`📍 ${lat.toFixed(6)}, ${lng.toFixed(6)}`).openPopup();
                }

                onLocationPick(lat, lng);
            });
        };

        init();

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
                markerRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When lat/lng props change externally, re-center and move marker
    useEffect(() => {
        if (!mapRef.current || !latitude || !longitude) return;
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);
        if (isNaN(lat) || isNaN(lng)) return;

        mapRef.current.setView([lat, lng], 15);
        if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
        }
    }, [latitude, longitude]);

    return (
        <div
            ref={containerRef}
            style={{ height: '320px', width: '100%', cursor: 'crosshair' }}
            className="rounded-b-xl"
        />
    );
};

export default MapPicker;
